/**
 * Configuration selon l'environnement
 *
 * Modes disponibles:
 * - Dev       : localhost, HTTP, développement local
 * - test_prod : IP locale, HTTPS, test réseau local (visioconférence LAN)
 * - Prod      : Domaine/IP publique, HTTPS, production
 */

const MODE = process.env.REACT_APP_ENV || "Dev";
const PORT = process.env.REACT_APP_PORT || 8080;
const LOCAL_IP = process.env.REACT_APP_LOCAL_IP;
const PROD_DOMAIN = process.env.REACT_APP_PROD_DOMAIN || "localhost";

// Configuration de base partagée
const baseConfig = {
  port: PORT,
  turnServers: [
    {
      urls: ["stun:stun.l.google.com:19302"]
    }
  ]
};

// Configurations spécifiques par environnement
const configs = {
  // ============================================
  // MODE DÉVELOPPEMENT (localhost)
  // ============================================
  Dev: {
    ...baseConfig,
    environment: "Dev",
    protocol: "http",
    host: "localhost",
    domain: process.env.REACT_APP_API_DEV || `http://localhost:${PORT}`,
    wsProtocol: "ws",
    isSecure: false,
  },

  // ============================================
  // MODE TEST PRODUCTION (réseau local / LAN)
  // Simule la production avec HTTPS + IP locale
  // Utile pour tester la visioconférence en LAN
  // ============================================
  test_prod: {
    ...baseConfig,
    environment: "test_prod",
    protocol: "https",
    host: LOCAL_IP,
    domain:
      process.env.REACT_APP_API_TEST_PROD || `https://${LOCAL_IP}:${PORT}`,
    wsProtocol: "wss",
    isSecure: true,
    turnServers: [
      {
        urls: [
          "stun:stun.l.google.com:19302",
          "stun:stun1.l.google.com:19302"
        ]
      }
    ]
  },

  // ============================================
  // MODE PRODUCTION
  // ============================================
  Prod: {
    ...baseConfig,
    environment: "Prod",
    protocol: "https",
    host: PROD_DOMAIN,
    domain: process.env.REACT_APP_API_PROD || `https://${PROD_DOMAIN}:${PORT}`,
    wsProtocol: "wss",
    isSecure: true,
    turnServers: [
      {
        urls: [
          "stun:stun.l.google.com:19302",
          "stun:stun1.l.google.com:19302"
        ]
      }
      // En production, les credentials TURN sont récupérés dynamiquement
      // via l'API /api/auth/turn-credentials
    ]
  },
};

// Récupérer la config selon le mode
export const env = configs[MODE] || configs.Dev;

// Log en développement
if (MODE === "Dev") {
  console.log(`[CONFIG] Mode: ${env.environment}`);
  console.log(`[CONFIG] API: ${env.domain}`);
}

// Export pour usage dans les services WebRTC
// Récupère les credentials TURN dynamiques via l'API protégée (session), puis
// renvoie la liste complète des serveurs ICE : STUN (fallback) + TURN (dynamique).
export const fetchTurnCredentials = async () => {
  const response = await fetch(`${env.domain}/api/auth/turn-credentials`, {
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error(
      `Échec récupération credentials TURN (HTTP ${response.status})`
    );
  }
  const data = await response.json();
  return {
    urls: data.urls,
    username: data.username,
    credential: data.credential,
    expiresAt: data.expiresAt,
  };
};

export const getTurnServers = async () => {
  const stunServers = [
    {
      urls: ["stun:stun.l.google.com:19302", "stun:stun1.l.google.com:19302"],
    },
  ];

  try {
    const creds = await fetchTurnCredentials();
    return [
      ...stunServers,
      {
        urls: creds.urls,
        username: creds.username,
        credential: creds.credential,
      },
    ];
  } catch (err) {
    console.warn(
      "[TURN] Credentials dynamiques indisponibles, STUN seul utilisé :",
      err.message
    );
    return stunServers;
  }
};
